import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import { isSuperUser as checkSuperUser, isManager as checkManager, isHR as checkHR, isAccounting as checkAccounting } from '@/lib/permissions';
import api from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  ReferenceLine,
  ComposedChart,
  Cell,
} from 'recharts';
import {
  Calendar,
  ArrowLeft,
  ArrowRightLeft,
  Plus,
  X,
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
} from 'lucide-react';

// Import the UserStats interface from the main Statistics component
interface UserStats {
  user_info?: {
    id: number;
    name: string;
    email: string;
    firm: string;
    salary?: number;
    yearly_target?: number;
  };
  period?: {
    type: string;
    start_date: string;
    end_date: string;
  };
  time_metrics?: {
    total_hours_actual: number;
    total_hours_projected: number;
    total_hours: number;
    billable_hours_actual: number;
    billable_hours_projected: number;
    billable_hours: number;
    non_billable_hours_actual: number;
    non_billable_hours_projected: number;
    non_billable_hours: number;
    entries_count_actual: number;
    entries_count_projected: number;
    entries_count: number;
    utilization_rate: number;
    average_hours_per_entry: number;
    days_worked: number;
    average_hours_per_day: number;
    average_billable_hours_per_day: number;
  };
  financial_metrics?: {
    total_revenue_actual: number;
    total_revenue_projected: number;
    total_revenue: number;
    billable_revenue_actual: number;
    billable_revenue_projected: number;
    billable_revenue: number;
    average_rate: number;
    max_rate: number;
    min_rate: number;
    revenue_per_hour: number;
    wip_hours: number;
  };
  productivity_metrics?: {
    work_frequency: {
      total_period_days: number;
      days_worked: number;
      work_frequency_rate: number;
    };
    daily_productivity: {
      avg_daily_hours: number;
      max_daily_hours: number;
      min_daily_hours: number;
      avg_daily_revenue: number;
      consistency_score: number;
    };
    entry_analysis: {
      avg_entry_size: number;
      max_entry_size: number;
      min_entry_size: number;
      small_entries: number;
      large_entries: number;
    };
    efficiency_score: number;
  };
  client_work?: {
    clients_worked_on: number;
    client_distribution: Array<{
      matter__clients__name: string | null;
      matter__clients__id: number;
      hours: number;
      revenue: number;
      entries: number;
      matters_count: number;
      percentage: number;
    }>;
    client_diversity_score: number;
  };
  matter_work?: {
    matters_worked_on: number;
    matter_distribution: Array<{
      matter__name: string | null;
      matter__id: number;
      matter__budget: number;
      hours: number;
      revenue: number;
      entries: number;
      last_worked: string | null;
      budget_utilization: number;
    }>;
    active_matters: number;
  };
  target_tracking?: {
    yearly_target: number;
    ytd_billable_hours: number;
    expected_hours: number;
    target_progress_percentage: number;
    hours_ahead_behind: number;
    monthly_target: number;
    quarterly_target: number;
  };
  monthly_breakdown?: Array<{
    month: string;
    month_short: string;
    month_num: number;
    billable_hours: number;
    non_billable_hours: number;
    total_hours: number;
    revenue: number;
    entries: number;
  }>;
  year_comparison?: {
    current_year: {
      label: string;
      data: Array<{
        month: string;
        month_short: string;
        month_num: number;
        billable_hours: number;
        non_billable_hours: number;
        total_hours: number;
        revenue: number;
        entries: number;
      }>;
    };
    previous_year: {
      label: string;
      data: Array<{
        month: string;
        month_short: string;
        month_num: number;
        billable_hours: number;
        non_billable_hours: number;
        total_hours: number;
        revenue: number;
        entries: number;
      }>;
    };
  };
  weekly_patterns?: Array<{
    day: string;
    day_index: number;
    billable_hours: number;
    non_billable_hours: number;
    total_hours: number;
    avg_hours: number;
    revenue: number;
    entries: number;
  }>;
  comparison_period?: UserStats;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
  new Intl.NumberFormat('en-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);

const fmt2 = (v: number) =>
  new Intl.NumberFormat('en-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(v);

const COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

// ─── MiniKPI sub-component ───────────────────────────────────────────────────

function MiniKPI({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold leading-tight mt-0.5">{value}</p>
      {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userGroups = user?.groups || [];
  const { users, fetchUsers } = useStore();
  
  // User role and permission flags
  const isSuperUser = checkSuperUser(userGroups);
  const isManager = checkManager(userGroups);
  const isHR = checkHR(userGroups);
  const isAccounting = checkAccounting(userGroups);

  const canViewUserStats = isSuperUser || isManager || isHR;
  const canViewFinancialData = isSuperUser || isAccounting;
  const preferTableView = !!(user?.preferences as Record<string, any> | undefined)?.prefer_table_view;
  const { hideAmounts, amountClassName } = useHideAmounts();

  // State
  const [period, setPeriod] = useState('year');
  const [isLoading, setIsLoading] = useState(true);
  const [userStats, setUserStats] = useState<UserStats | null>(null);

  // ── Custom date range state ──
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // ── Period comparison state (compare to another arbitrary period) ──
  const [compareStart, setCompareStart] = useState('');
  const [compareEnd, setCompareEnd] = useState('');

  // ── Target chart view mode (monthly vs cumulative) ──
  const [isCumulativeTarget, setIsCumulativeTarget] = useState(false);

  // ── User comparison state ──
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [compareData, setCompareData] = useState<Record<number, UserStats>>({});
  const [showCompareSelector, setShowCompareSelector] = useState(false);

  const primaryUserId = parseInt(userId || '0');
  const selectedUser = users.find(u => u.id === primaryUserId);

  // Ensure users are loaded for the compare dropdown
  useEffect(() => {
    if (users.length === 0) {
      fetchUsers();
    }
  }, []);

  // Initialise compare IDs from URL ?compare=2,3
  useEffect(() => {
    const cp = searchParams.get('compare');
    if (cp) {
      setCompareIds(cp.split(',').map(Number).filter((n) => n && n !== primaryUserId));
    }
  }, []);

  // ``<input type="date">`` accepts intermediate states like
  // ``0202-12-31`` while the user is still typing the year, and fires
  // ``onChange`` for each keystroke. We need a sanity check before the
  // value reaches the API; otherwise the backend (rightly) rejects
  // start > end and the panel shows zeros.
  const isPlausibleIsoDate = (v: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const year = parseInt(v.slice(0, 4), 10);
    if (year < 1900 || year > 2100) return false;
    const t = Date.parse(v);
    return !Number.isNaN(t);
  };

  const customRangeIsValid =
    period !== 'custom' ||
    (isPlausibleIsoDate(customStart) &&
      isPlausibleIsoDate(customEnd) &&
      customStart <= customEnd);

  const compareRangeIsValid =
    !compareStart ||
    !compareEnd ||
    (isPlausibleIsoDate(compareStart) &&
      isPlausibleIsoDate(compareEnd) &&
      compareStart <= compareEnd);

  // Build the query string for API calls
  const buildApiQuery = (uid: number | string) => {
    let qs = `${ROUTES.API.STATISTICS}users/${uid}/?detail=full`;
    if (period === 'custom' && customStart && customEnd) {
      qs += `&period=custom&start_date=${customStart}&end_date=${customEnd}`;
    } else {
      qs += `&period=${period}`;
    }
    // Append comparison period dates if set
    if (compareStart && compareEnd) {
      qs += `&compare_start_date=${compareStart}&compare_end_date=${compareEnd}`;
    }
    return qs;
  };

  useEffect(() => {
    if (!canViewUserStats || !userId) {
      navigate('/statistics/team');
      return;
    }
    // Don't fetch with an in-progress / invalid date range.
    if (!customRangeIsValid || !compareRangeIsValid) return;
    fetchUserStats();
  }, [period, userId, canViewUserStats, navigate, customStart, customEnd, compareStart, compareEnd, customRangeIsValid, compareRangeIsValid]);

  // Re-fetch comparison data when compareIds change
  useEffect(() => {
    if (compareIds.length === 0) {
      setCompareData({});
      return;
    }
    if (!customRangeIsValid || !compareRangeIsValid) return;
    fetchCompareData();
  }, [compareIds, period, customStart, customEnd, compareStart, compareEnd, customRangeIsValid, compareRangeIsValid]);

  const fetchUserStats = async () => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      const userResponse = await api.get(buildApiQuery(userId));
      setUserStats(userResponse.data);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch user statistics',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompareData = async () => {
    try {
      const results: Record<number, UserStats> = {};
      await Promise.all(
        compareIds.map(async (id) => {
          const res = await api.get(buildApiQuery(id));
          results[id] = res.data;
        }),
      );
      setCompareData(results);
    } catch {
      toast({ title: 'Error', description: 'Failed to fetch comparison data', variant: 'destructive' });
    }
  };

  const addCompare = (id: number) => {
    if (id !== primaryUserId && !compareIds.includes(id)) {
      setCompareIds((prev) => [...prev, id]);
    }
    setShowCompareSelector(false);
  };

  const removeCompare = (id: number) => {
    setCompareIds((prev) => prev.filter((x) => x !== id));
  };

  // All users in the comparison (primary + compare)
  const allUserStats: { id: number; name: string; stats: UserStats | null; color: string }[] = [
    {
      id: primaryUserId,
      name: selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : userStats?.user_info?.name || 'User',
      stats: userStats,
      color: COLORS[0],
    },
    ...compareIds.map((id, i) => {
      const u = users.find((x) => x.id === id);
      return {
        id,
        name: u ? `${u.first_name} ${u.last_name}` : compareData[id]?.user_info?.name || `User ${id}`,
        stats: compareData[id] || null,
        color: COLORS[(i + 1) % COLORS.length],
      };
    }),
  ];

  const isComparing = compareIds.length > 0;
  const isComparingPeriod = !!(compareStart && compareEnd);
  const isYearView = period === 'year';
  const compPeriod = userStats?.comparison_period;

  // Period label helper
  const periodLabel = period === 'custom' && customStart && customEnd
    ? `${customStart} → ${customEnd}`
    : period === 'year' ? 'This Year' : period === 'quarter' ? 'This Quarter' : 'This Month';
  const comparePeriodLabel = compareStart && compareEnd
    ? `${compareStart} → ${compareEnd}` : '';

  if (!canViewUserStats) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <p className="text-muted-foreground">You don't have permission to view this data.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/statistics/team')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {selectedUser?.first_name} {selectedUser?.last_name}
              </h1>
              <p className="text-muted-foreground text-sm">{selectedUser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Compare chips */}
            {compareIds.map((cid) => {
              const cu = users.find((x) => x.id === cid);
              return (
                <Badge key={cid} variant="secondary" className="gap-1 pr-1">
                  {cu ? `${cu.first_name} ${cu.last_name}` : `User ${cid}`}
                  <button onClick={() => removeCompare(cid)} className="ml-1 hover:bg-muted rounded-full p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}

            {/* Print report — opens a new tab with the printable page, which
                auto-invokes window.print() once data is loaded. Forward
                custom date range (and any active comparison) so the
                printable mirrors the on-screen view. */}
            {(() => {
              const buildReportUrl = (mode: 'admin' | 'user') => {
                const qs = new URLSearchParams({ mode, period });
                if (period === 'custom' && customStart && customEnd) {
                  qs.set('start_date', customStart);
                  qs.set('end_date', customEnd);
                }
                if (compareStart && compareEnd) {
                  qs.set('compare_start_date', compareStart);
                  qs.set('compare_end_date', compareEnd);
                }
                return `/statistics/users/${primaryUserId}/report?${qs.toString()}`;
              };
              return (
                <div className="inline-flex items-center rounded-md border">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(buildReportUrl('admin'), '_blank', 'noopener')}
                    className="rounded-none rounded-l-md"
                  >
                    Print · Admin
                  </Button>
                  <div className="w-px self-stretch bg-border" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(buildReportUrl('user'), '_blank', 'noopener')}
                    className="rounded-none rounded-r-md"
                  >
                    Print · User
                  </Button>
                </div>
              );
            })()}

            {/* Add compare button */}
            {!showCompareSelector ? (
              <Button variant="outline" size="sm" onClick={() => setShowCompareSelector(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Compare
              </Button>
            ) : (
              <Select onValueChange={(v) => addCompare(Number(v))}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select employee…" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((u) => u.id !== primaryUserId && !compareIds.includes(u.id))
                    .map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}

            <Select value={period} onValueChange={(v) => {
              setPeriod(v);
              if (v !== 'custom') { setCustomStart(''); setCustomEnd(''); }
            }}>
              <SelectTrigger className="w-40">
                <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="quarter">Quarterly</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom date range inputs */}
            {period === 'custom' && (
              <div className="flex items-center gap-1">
                <DatePicker
                  minDate="1900-01-01"
                  maxDate="2100-12-31"
                  value={customStart}
                  onChange={setCustomStart}
                  className={`w-36 h-9 text-sm ${!customRangeIsValid && customStart ? 'border-rose-400' : ''}`}
                />
                <span className="text-muted-foreground text-xs">→</span>
                <DatePicker
                  minDate="1900-01-01"
                  maxDate="2100-12-31"
                  value={customEnd}
                  onChange={setCustomEnd}
                  className={`w-36 h-9 text-sm ${!customRangeIsValid && customEnd ? 'border-rose-400' : ''}`}
                />
              </div>
            )}

            {/* Compare period button */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={isComparingPeriod ? 'secondary' : 'outline'} size="sm" className="gap-1.5">
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                  {isComparingPeriod ? 'Comparing' : 'Compare Period'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-4" align="end">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Compare against another period</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-muted-foreground">Start</label>
                      <DatePicker value={compareStart} onChange={setCompareStart} className="h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">End</label>
                      <DatePicker value={compareEnd} onChange={setCompareEnd} className="h-8 text-sm" />
                    </div>
                  </div>
                  {isComparingPeriod && (
                    <Button variant="ghost" size="sm" className="w-full" onClick={() => { setCompareStart(''); setCompareEnd(''); }}>
                      Clear comparison
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Invalid-range warning — surfaces the cause when the request was
            blocked client-side (e.g. a 4-digit year that's still being
            typed). */}
        {period === 'custom' && (customStart || customEnd) && !customRangeIsValid && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-400">
            Enter a valid date range (start ≤ end, year between 1900 and 2100) to load statistics.
          </div>
        )}
        {compareStart && compareEnd && !compareRangeIsValid && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-400">
            Comparison range is invalid — start must be ≤ end and within 1900–2100.
          </div>
        )}

        {/* ── KPI summary strip ── */}
        {!isLoading && userStats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <MiniKPI label="Billable Hours" value={`${(userStats.time_metrics?.billable_hours_actual ?? 0).toFixed(0)}h`} />
            <MiniKPI label="Total Hours" value={`${(userStats.time_metrics?.total_hours_actual ?? 0).toFixed(0)}h`} />
            <MiniKPI label="Billable vs Non-Billable" value={`${(userStats.time_metrics?.utilization_rate ?? 0).toFixed(0)}%`} />
            <MiniKPI label="Days Worked" value={String(userStats.time_metrics?.days_worked ?? 0)} />
            {canViewFinancialData && (
              <>
                <MiniKPI label="Revenue" value={hideAmounts ? '•••' : fmt(userStats.financial_metrics?.total_revenue_actual ?? 0)} />
                {(userStats.user_info?.salary ?? 0) > 0 && (
                  <MiniKPI
                    label="Rev / Salary"
                    value={hideAmounts ? '•••' : `${(((userStats.financial_metrics?.total_revenue_actual ?? 0) / (userStats.user_info!.salary!)) * 100).toFixed(0)}%`}
                    subtitle={hideAmounts ? '•••' : `Salary: ${fmt(userStats.user_info!.salary!)}`}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* ── Profitability Overview Cards ── */}
        {!isLoading && userStats && canViewFinancialData && (userStats.user_info?.salary ?? 0) > 0 && (() => {
          const salary = userStats.user_info!.salary!;
          const revenue = userStats.financial_metrics?.total_revenue_actual ?? 0;

          // Prorate salary to period
          const startDate = userStats.period?.start_date ? new Date(userStats.period.start_date) : null;
          const endDate = userStats.period?.end_date ? new Date(userStats.period.end_date) : null;
          let periodDays = 365;
          if (startDate && endDate) {
            periodDays = Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);
          }
          const proratedSalary = salary * (periodDays / 365);
          const profit = revenue - proratedSalary;
          const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
          const roi = proratedSalary > 0 ? (revenue / proratedSalary) * 100 : 0;
          const isProfitable = profit >= 0;

          return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className={isProfitable ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/30' : 'border-rose-200 bg-rose-50/50 dark:border-rose-900 dark:bg-rose-950/30'}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    {isProfitable ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-rose-600" />}
                    <p className="text-sm font-medium text-muted-foreground">Net Profit</p>
                  </div>
                  <p className={`text-2xl font-bold ${amountClassName} ${isProfitable ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                    {hideAmounts ? '•••' : `${profit >= 0 ? '+' : ''}${fmt(profit)}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isProfitable ? 'Profitable' : 'Loss'} this period
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Wallet className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">Period Salary Cost</p>
                  </div>
                  <p className={`text-2xl font-bold ${amountClassName}`}>
                    {hideAmounts ? '•••' : fmt(proratedSalary)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {hideAmounts ? '•••' : `${fmt(salary)}/year`} · {periodDays} days
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">ROI</p>
                  </div>
                  <p className={`text-2xl font-bold ${roi >= 100 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                    {hideAmounts ? '•••' : `${roi.toFixed(0)}%`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Revenue vs salary cost
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium text-muted-foreground">Profit Margin</p>
                  </div>
                  <p className={`text-2xl font-bold ${profitMargin >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                    {hideAmounts ? '•••' : `${profitMargin.toFixed(1)}%`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Of revenue retained as profit
                  </p>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* ── Target Tracking Graph: Billable Hours vs Goal ── */}
        {!isLoading && userStats && (userStats.target_tracking?.yearly_target ?? 0) > 0 && userStats.monthly_breakdown && userStats.monthly_breakdown.length > 0 && !isComparing && (() => {
          const yearlyTarget = userStats.target_tracking!.yearly_target;
          const breakdown = userStats.monthly_breakdown!;

          // Choose interval based on period length
          const startDate = userStats.period?.start_date ? new Date(userStats.period.start_date) : null;
          const endDate = userStats.period?.end_date ? new Date(userStats.period.end_date) : null;
          if (startDate && endDate) {
            Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);
          }

          // Monthly target prorated over the period
          const targetPerMonth = yearlyTarget / 12;

          // Build chart data — monthly or cumulative billable hours vs target
          let cumActual = 0;
          let cumTarget = 0;
          const chartData = breakdown.map((m) => {
            cumActual += m.billable_hours;
            cumTarget += targetPerMonth;
            const monthlyActual = m.billable_hours;
            const ahead = isCumulativeTarget
              ? cumActual >= cumTarget
              : monthlyActual >= targetPerMonth;
            return {
              label: m.month_short,
              actual: isCumulativeTarget
                ? parseFloat(cumActual.toFixed(1))
                : parseFloat(monthlyActual.toFixed(1)),
              target: isCumulativeTarget
                ? parseFloat(cumTarget.toFixed(1))
                : parseFloat(targetPerMonth.toFixed(1)),
              ahead,
            };
          });

          const lastCumActual = cumActual;
          const lastCumTarget = cumTarget;
          const lastPoint = chartData[chartData.length - 1];
          const isAheadOfTarget = lastCumActual >= lastCumTarget;

          return (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Billable Hours vs Target</CardTitle>
                    <CardDescription>
                      {(userStats.target_tracking?.ytd_billable_hours ?? 0).toFixed(0)}h done · Target: {yearlyTarget.toFixed(0)}h/year
                      {lastPoint && (
                        <span className={`ml-2 font-semibold ${isAheadOfTarget ? 'text-emerald-600' : 'text-rose-600'}`}>
                          ({isAheadOfTarget ? '+' : ''}{(lastCumActual - lastCumTarget).toFixed(0)}h)
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Cumulative</span>
                      <Switch
                        checked={isCumulativeTarget}
                        onCheckedChange={setIsCumulativeTarget}
                      />
                    </div>
                    <Badge variant={isAheadOfTarget ? 'default' : 'destructive'} className={isAheadOfTarget ? 'bg-emerald-600' : ''}>
                      {isAheadOfTarget ? 'On Track' : 'Behind'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => `${Number(value).toFixed(1)}h`} />
                    <Legend />
                    {isCumulativeTarget && (
                      <ReferenceLine
                        y={yearlyTarget}
                        stroke="#ef4444"
                        strokeDasharray="6 4"
                        strokeWidth={2}
                        label={{ value: `Year target: ${yearlyTarget}h`, position: 'insideTopRight', fill: '#ef4444', fontSize: 11 }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke="#ef4444"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name={isCumulativeTarget ? 'Expected Progress' : 'Monthly Target'}
                      dot={false}
                    />
                    <Bar
                      dataKey="actual"
                      name={isCumulativeTarget ? 'Cumulative Billable Hours' : 'Monthly Billable Hours'}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    >
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.ahead ? '#16a34a' : '#6366f1'} />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })()}

        {/* ── Period comparison card ── */}
        {isComparingPeriod && compPeriod && !isLoading && userStats && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Period Comparison</CardTitle>
              </div>
              <CardDescription>
                {periodLabel} vs {comparePeriodLabel}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead className="text-right">{periodLabel}</TableHead>
                      <TableHead className="text-right">{comparePeriodLabel}</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const rows: { label: string; current: number; compare: number; format: 'hours' | 'pct' | 'currency' | 'number' }[] = [
                        { label: 'Billable Hours', current: userStats.time_metrics?.billable_hours_actual ?? 0, compare: compPeriod.time_metrics?.billable_hours_actual ?? 0, format: 'hours' },
                        { label: 'Total Hours', current: userStats.time_metrics?.total_hours_actual ?? 0, compare: compPeriod.time_metrics?.total_hours_actual ?? 0, format: 'hours' },
                        { label: 'Utilization', current: userStats.time_metrics?.utilization_rate ?? 0, compare: compPeriod.time_metrics?.utilization_rate ?? 0, format: 'pct' },
                        { label: 'Days Worked', current: userStats.time_metrics?.days_worked ?? 0, compare: compPeriod.time_metrics?.days_worked ?? 0, format: 'number' },
                      ];
                      if (canViewFinancialData) {
                        rows.push(
                          { label: 'Revenue', current: userStats.financial_metrics?.total_revenue_actual ?? 0, compare: compPeriod.financial_metrics?.total_revenue_actual ?? 0, format: 'currency' },
                          { label: 'Avg Rate', current: userStats.financial_metrics?.average_rate ?? 0, compare: compPeriod.financial_metrics?.average_rate ?? 0, format: 'currency' },
                        );
                      }
                      const fmtVal = (v: number, f: string) => {
                        if (f === 'hours') return `${v.toFixed(1)}h`;
                        if (f === 'pct') return `${v.toFixed(0)}%`;
                        if (f === 'currency') return hideAmounts ? '•••' : fmt(v);
                        return String(Math.round(v));
                      };
                      return rows.map((r) => {
                        const delta = r.compare !== 0 ? ((r.current - r.compare) / r.compare) * 100 : r.current > 0 ? 100 : 0;
                        const isUp = delta > 0;
                        return (
                          <TableRow key={r.label}>
                            <TableCell className="font-medium">{r.label}</TableCell>
                            <TableCell className="text-right font-semibold">{fmtVal(r.current, r.format)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{fmtVal(r.compare, r.format)}</TableCell>
                            <TableCell className={`text-right font-medium ${isUp ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                              {delta !== 0 ? `${isUp ? '+' : ''}${delta.toFixed(0)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Comparison summary table ── */}
        {isComparing && !isLoading && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Billable Hours</TableHead>
                      <TableHead className="text-right">Total Hours</TableHead>
                      <TableHead className="text-right">Utilization</TableHead>
                      <TableHead className="text-right">Days</TableHead>
                      {canViewFinancialData && (
                        <>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Avg Rate</TableHead>
                        </>
                      )}
                      <TableHead className="text-right">Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUserStats.map((u) => {
                      const s = u.stats;
                      return (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: u.color }} />
                              <span className="font-medium">{u.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{(s?.time_metrics?.billable_hours_actual ?? 0).toFixed(1)}h</TableCell>
                          <TableCell className="text-right">{(s?.time_metrics?.total_hours_actual ?? 0).toFixed(1)}h</TableCell>
                          <TableCell className="text-right">{(s?.time_metrics?.utilization_rate ?? 0).toFixed(0)}%</TableCell>
                          <TableCell className="text-right">{s?.time_metrics?.days_worked ?? 0}</TableCell>
                          {canViewFinancialData && (
                            <>
                              <TableCell className={`text-right font-semibold ${amountClassName}`}>{hideAmounts ? '•••' : fmt(s?.financial_metrics?.total_revenue_actual ?? 0)}</TableCell>
                              <TableCell className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : `${fmt(s?.financial_metrics?.average_rate ?? 0)}/h`}</TableCell>
                            </>
                          )}
                          <TableCell className="text-right">
                            {(s?.target_tracking?.target_progress_percentage ?? 0).toFixed(0)}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Charts grid (2-col on desktop) ── */}
        {!isLoading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Year-over-Year Hours — only in year view, not during user comparison */}
            {isYearView && !isComparing && userStats?.year_comparison?.current_year?.data && userStats.year_comparison.current_year.data.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Hours — Year over Year</CardTitle>
                  <CardDescription>
                    {userStats.year_comparison.previous_year?.label} vs {userStats.year_comparison.current_year.label}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {preferTableView ? (
                    <div className="overflow-x-auto max-h-72 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">{userStats.year_comparison.previous_year?.label ?? 'Previous'}</TableHead>
                            <TableHead className="text-right">{userStats.year_comparison.current_year.label}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userStats.year_comparison.current_year.data.map((cur, i) => {
                            const prev = userStats.year_comparison?.previous_year?.data?.[i];
                            return (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{cur.month_short}</TableCell>
                                <TableCell className="text-right text-muted-foreground">{(prev?.billable_hours ?? 0).toFixed(1)}h</TableCell>
                                <TableCell className="text-right">{cur.billable_hours.toFixed(1)}h</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={userStats.year_comparison.current_year.data.map((cur, i) => {
                          const prev = userStats.year_comparison?.previous_year?.data?.[i];
                          return { month: cur.month_short, current: cur.billable_hours, previous: prev?.billable_hours ?? 0 };
                        })}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="previous" fill="#c7d2fe" name={userStats.year_comparison.previous_year?.label ?? 'Previous'} radius={[3, 3, 0, 0]} />
                        <Bar dataKey="current" fill="#6366f1" name={userStats.year_comparison.current_year.label} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Billable vs Non-Billable — only in year view, not during user comparison */}
            {isYearView && !isComparing && userStats?.monthly_breakdown && userStats.monthly_breakdown.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Billable vs Non-Billable</CardTitle>
                  <CardDescription>Monthly hours breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  {preferTableView ? (
                    <div className="overflow-x-auto max-h-72 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">Billable</TableHead>
                            <TableHead className="text-right">Non-Billable</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userStats.monthly_breakdown.map((m, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{m.month_short}</TableCell>
                              <TableCell className="text-right">{m.billable_hours.toFixed(1)}h</TableCell>
                              <TableCell className="text-right text-muted-foreground">{m.non_billable_hours.toFixed(1)}h</TableCell>
                              <TableCell className="text-right font-semibold">{m.total_hours.toFixed(1)}h</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={userStats.monthly_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="month_short" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="billable_hours" stackId="h" fill="#22c55e" name="Billable" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="non_billable_hours" stackId="h" fill="#f97316" name="Non-Billable" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Revenue YoY — only in year view, not during user comparison */}
            {isYearView && !isComparing && canViewFinancialData && userStats?.year_comparison?.current_year?.data && userStats.year_comparison.current_year.data.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Revenue — Year over Year</CardTitle>
                  <CardDescription>
                    {userStats.year_comparison.previous_year?.label} vs {userStats.year_comparison.current_year.label}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {preferTableView ? (
                    <div className="overflow-x-auto max-h-72 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">{userStats.year_comparison.previous_year?.label ?? 'Previous'}</TableHead>
                            <TableHead className="text-right">{userStats.year_comparison.current_year.label}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userStats.year_comparison.current_year.data.map((cur, i) => {
                            const prev = userStats.year_comparison?.previous_year?.data?.[i];
                            return (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{cur.month_short}</TableCell>
                                <TableCell className={`text-right text-muted-foreground ${amountClassName}`}>{hideAmounts ? '•••' : fmt(prev?.revenue ?? 0)}</TableCell>
                                <TableCell className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : fmt(cur.revenue)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={userStats.year_comparison.current_year.data.map((cur, i) => {
                          const prev = userStats.year_comparison?.previous_year?.data?.[i];
                          return { month: cur.month_short, current: cur.revenue, previous: prev?.revenue ?? 0 };
                        })}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => hideAmounts ? '•••' : `€${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => hideAmounts ? '•••' : fmt2(Number(value))} />
                        <Legend />
                        <Bar dataKey="previous" fill="#a7f3d0" name={userStats.year_comparison.previous_year?.label ?? 'Previous'} radius={[3, 3, 0, 0]} />
                        <Bar dataKey="current" fill="#10b981" name={userStats.year_comparison.current_year.label} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Weekly Work Pattern removed — not useful for final results */}

            {/* ── Comparison: Monthly hours overlay ── */}
            {isComparing && (() => {
              const primary = userStats?.monthly_breakdown ?? [];
              if (primary.length === 0) return null;

              if (preferTableView) {
                return (
                  <Card className="lg:col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Hours Comparison — Monthly</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Month</TableHead>
                              {allUserStats.map((u) => (
                                <TableHead key={u.id} className="text-right">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: u.color }} />
                                    {u.name.split(' ')[0]}
                                  </div>
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {primary.map((m, mi) => (
                              <TableRow key={mi}>
                                <TableCell className="font-medium">{m.month_short}</TableCell>
                                {allUserStats.map((u, ui) => {
                                  const data = ui === 0 ? primary : (u.stats?.monthly_breakdown ?? []);
                                  const match = ui === 0 ? m : data.find((cm) => cm.month_short === m.month_short);
                                  return <TableCell key={u.id} className="text-right">{(match?.billable_hours ?? 0).toFixed(1)}h</TableCell>;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              const merged = primary.map((m) => {
                const row: Record<string, number | string> = { month: m.month_short };
                const pName = allUserStats[0]?.name?.split(' ')[0] ?? 'You';
                row[pName] = m.billable_hours;
                for (const cu of allUserStats.slice(1)) {
                  const cData = cu.stats?.monthly_breakdown ?? [];
                  const match = cData.find((cm) => cm.month_short === m.month_short);
                  row[cu.name.split(' ')[0]] = match?.billable_hours ?? 0;
                }
                return row;
              });
              return (
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Hours Comparison — Monthly</CardTitle>
                    <CardDescription>Monthly billable hours across selected employees</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={merged}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        {allUserStats.map((u) => (
                          <Line key={u.id} type="monotone" dataKey={u.name.split(' ')[0]} stroke={u.color} strokeWidth={2} dot={{ r: 3 }} name={u.name} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Revenue comparison */}
            {isComparing && canViewFinancialData && (() => {
              const primary = userStats?.monthly_breakdown ?? [];
              if (primary.length === 0) return null;

              if (preferTableView) {
                return (
                  <Card className="lg:col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Revenue Comparison — Monthly</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Month</TableHead>
                              {allUserStats.map((u) => (
                                <TableHead key={u.id} className="text-right">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: u.color }} />
                                    {u.name.split(' ')[0]}
                                  </div>
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {primary.map((m, mi) => (
                              <TableRow key={mi}>
                                <TableCell className="font-medium">{m.month_short}</TableCell>
                                {allUserStats.map((u, ui) => {
                                  const data = ui === 0 ? primary : (u.stats?.monthly_breakdown ?? []);
                                  const match = ui === 0 ? m : data.find((cm) => cm.month_short === m.month_short);
                                  return <TableCell key={u.id} className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : fmt(match?.revenue ?? 0)}</TableCell>;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              const merged = primary.map((m) => {
                const row: Record<string, number | string> = { month: m.month_short };
                const pName = allUserStats[0]?.name?.split(' ')[0] ?? 'You';
                row[pName] = m.revenue;
                for (const cu of allUserStats.slice(1)) {
                  const cData = cu.stats?.monthly_breakdown ?? [];
                  const match = cData.find((cm) => cm.month_short === m.month_short);
                  row[cu.name.split(' ')[0]] = match?.revenue ?? 0;
                }
                return row;
              });
              return (
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Revenue Comparison — Monthly</CardTitle>
                    <CardDescription>Monthly revenue across selected employees</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={merged}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => hideAmounts ? '•••' : `€${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => hideAmounts ? '•••' : fmt2(Number(value))} />
                        <Legend />
                        {allUserStats.map((u) => (
                          <Line key={u.id} type="monotone" dataKey={u.name.split(' ')[0]} stroke={u.color} strokeWidth={2} dot={{ r: 3 }} name={u.name} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              );
            })()}

          </div>
        )}

        {/* ── Client & Matter tables ── */}
        {!isLoading && (
          <div className="space-y-6">
            {/* Matter Distribution — Enhanced: hours, billable_hours, revenue, % */}
            {userStats?.matter_work?.matter_distribution && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Matter Breakdown</CardTitle>
                  <CardDescription>
                    {userStats.matter_work.matters_worked_on} matters ({userStats.matter_work.active_matters} active)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Matter</TableHead>
                          <TableHead className="text-right">Total Hours</TableHead>
                          <TableHead className="text-right">Billable Hours</TableHead>
                          {canViewFinancialData && <TableHead className="text-right">Amount</TableHead>}
                          <TableHead className="text-right">Entries</TableHead>
                          <TableHead className="text-right">% of Period</TableHead>
                          {canViewFinancialData && <TableHead className="text-right">Budget %</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const totalBillableHours = userStats.matter_work!.matter_distribution.reduce((s, m) => s + (m.hours || 0), 0) || 1;
                          return userStats.matter_work!.matter_distribution
                            .filter((m) => m.hours > 0)
                            .sort((a, b) => b.revenue - a.revenue)
                            .map((m, i) => {
                              const pctOfPeriod = ((m.hours / totalBillableHours) * 100);
                              return (
                                <TableRow key={i}>
                                  <TableCell className="font-medium truncate max-w-[250px]">
                                    {m.matter__name || 'Unknown'}
                                  </TableCell>
                                  <TableCell className="text-right text-muted-foreground">{m.hours.toFixed(1)}h</TableCell>
                                  <TableCell className="text-right font-semibold">{m.hours.toFixed(1)}h</TableCell>
                                  {canViewFinancialData && (
                                    <TableCell className={`text-right font-semibold ${amountClassName}`}>
                                      {hideAmounts ? '•••' : fmt(m.revenue)}
                                    </TableCell>
                                  )}
                                  <TableCell className="text-right">{m.entries}</TableCell>
                                  <TableCell className="text-right text-muted-foreground">{pctOfPeriod.toFixed(1)}%</TableCell>
                                  {canViewFinancialData && (
                                    <TableCell className="text-right">
                                      {m.budget_utilization ? (
                                        <span className={m.budget_utilization > 100 ? 'text-rose-600 font-semibold' : m.budget_utilization > 80 ? 'text-amber-600' : 'text-emerald-600'}>
                                          {m.budget_utilization.toFixed(0)}%
                                        </span>
                                      ) : '—'}
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            });
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Client Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {userStats?.client_work?.client_distribution && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Client Distribution</CardTitle>
                    <CardDescription>{userStats.client_work.clients_worked_on} clients</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-72 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Client</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                            {canViewFinancialData && <TableHead className="text-right">Revenue</TableHead>}
                            <TableHead className="text-right">%</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userStats.client_work.client_distribution
                            .filter((c) => c.hours > 0)
                            .map((c, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-medium truncate max-w-[200px]">
                                  {c.matter__clients__name || 'Unknown'}
                                </TableCell>
                                <TableCell className="text-right">{c.hours.toFixed(1)}h</TableCell>
                                {canViewFinancialData && (
                                  <TableCell className={`text-right ${amountClassName}`}>{hideAmounts ? '•••' : fmt(c.revenue)}</TableCell>
                                )}
                                <TableCell className="text-right text-muted-foreground">
                                  {c.percentage.toFixed(0)}%
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Skeleton className="h-[340px] rounded-lg" />
              <Skeleton className="h-[340px] rounded-lg" />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}